import Cropper, { Area, MediaSize, Point } from 'react-easy-crop'
import { useCallback, useEffect, useState } from 'react'
import { UploadedPhotoWrapper, CropperWrapper } from './styles'
import photoPlaceholder from 'assets/images/unlimitedCard/photoPlaceholder.jpg'
import Icon from 'components/shared/Icon'
import { UnlimitedButton } from 'components/shared/Button/styles'
import InputSlider from 'components/shared/InputSlider'
import Text from 'components/shared/Text'
import Box from 'components/shared/Box'
import { FormattedMessage } from 'react-intl'
import getCroppedImg, { FileWithProps } from 'routes/unlimited-card/helpers'
import { setCardDetails, setImageDetails } from 'store/unlimited-slice'
import { useAppDispatch, useAppSelector } from 'store/hooks'

interface UploadedPhotoProps {
    index: number
    file: FileWithProps
    deletePhoto: () => void
    changePhoto: () => void
}

const UploadedPhoto = ({
    index,
    file,
    deletePhoto,
    changePhoto,
}: UploadedPhotoProps) => {
    const dispatch = useAppDispatch()
    const { cards: cartCards } = useAppSelector((state) => state.cart.items)
    const { cardBoxes } = useAppSelector((state) => state.unlimited)

    const [isEditMode, setIsEditMode] = useState(false)

    // eslint-disable-next-line id-length
    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(
        null
    )
    const [zoom, setZoom] = useState(1)
    const [maxZoom, setMaxZoom] = useState(0)

    const { image: croppedImage, originalImage } = cardBoxes[index]
    const isCartEmpty = !cartCards.length

    const MIN_WIDTH = 492
    const MIN_HEIGHT = 633

    const handleOnCropComplete = useCallback(
        (croppedArea, croppedAreaPixels) =>
            setCroppedAreaPixels(croppedAreaPixels),
        []
    )

    const handleSavingCroppedImage = async () => {
        if (!croppedAreaPixels) return

        try {
            const croppedImage = await getCroppedImg(
                file.preview,
                croppedAreaPixels
            )
            if (croppedImage) {
                // save edited/cropped image...
                dispatch(
                    setCardDetails({
                        field: 'image',
                        value: croppedImage,
                        index,
                    })
                )
                // ...and also original image with settings
                dispatch(
                    setImageDetails({
                        value: {
                            image: file.preview,
                            crop,
                            croppedAreaPixels,
                            zoom,
                            maxZoom,
                        },
                        index,
                    })
                )
            }
            setIsEditMode(false)
        } catch (error) {
            console.error(error)
        }
    }

    const handleMediaLoad = ({ naturalWidth, naturalHeight }: MediaSize) => {
        const maxZoomX = naturalWidth / MIN_WIDTH
        const maxZoomY = naturalHeight / MIN_HEIGHT
        const maxZoom = Math.min(maxZoomX, maxZoomY)
        setMaxZoom(maxZoom)
    }

    const preview = (
        <img
            src={croppedImage ? croppedImage : ''}
            alt="Cropped preview"
            onLoad={() => {
                // eslint-disable-next-line compat/compat
                croppedImage && URL.revokeObjectURL(croppedImage)
            }}
        />
    )

    useEffect(() => {
        if (!croppedAreaPixels) return

        // save image with restored settings
        if (!isCartEmpty && !croppedImage) {
            handleSavingCroppedImage()
        }

        if (maxZoom === 0) {
            const { width, height } = croppedAreaPixels
            const maxZoomX = width / MIN_WIDTH
            const maxZoomY = height / MIN_HEIGHT
            const maxZoom = Math.min(maxZoomX, maxZoomY)
            setMaxZoom(maxZoom)
        }
    }, [croppedAreaPixels])

    useEffect(() => {
        // on file upload...
        if (file) {
            if (!isCartEmpty && !croppedImage) {
                // ...restore image editing settings
                if (originalImage) {
                    const { crop, croppedAreaPixels, zoom, maxZoom } =
                        originalImage
                    crop && setCrop(crop)
                    setCroppedAreaPixels(croppedAreaPixels)
                    setZoom(zoom)
                    setMaxZoom(maxZoom)
                }
            } else {
                // ...or reset settings
                // eslint-disable-next-line id-length
                setCrop({ x: 0, y: 0 })
                setCroppedAreaPixels(null)
                setZoom(1)
                setMaxZoom(0)
            }
            // and turn on edit mode
            setIsEditMode(true)
        }
    }, [file])

    useEffect(() => {
        if (isEditMode)
            dispatch(
                setCardDetails({
                    field: 'image',
                    value: '',
                    index,
                })
            )
    }, [isEditMode])

    useEffect(
        () => () => {
            // eslint-disable-next-line compat/compat
            croppedImage && URL.revokeObjectURL(croppedImage)
        },
        []
    )

    return (
        <UploadedPhotoWrapper
            align={'center'}
            padding={8}
            mr={[0, 48]}
            gap={[8, 16]}
        >
            <CropperWrapper isFullWidth>
                {isEditMode ? (
                    <Cropper
                        image={file ? file.preview : photoPlaceholder}
                        showGrid={isEditMode}
                        crop={crop}
                        zoom={zoom}
                        maxZoom={maxZoom}
                        rotation={0}
                        aspect={MIN_WIDTH / MIN_HEIGHT}
                        objectFit="vertical-cover"
                        onCropChange={setCrop}
                        onCropComplete={handleOnCropComplete}
                        onZoomChange={setZoom}
                        onMediaLoaded={handleMediaLoad}
                    />
                ) : (
                    preview
                )}
            </CropperWrapper>
            {isEditMode ? (
                <>
                    <Box
                        direction="row"
                        isFullWidth
                        justify={'center'}
                        mt={20}
                        mb={8}
                    >
                        <InputSlider
                            min={100}
                            max={Math.floor(maxZoom * 100)}
                            value={zoom * 100}
                            onChange={(value) => setZoom(value / 100)}
                        />
                    </Box>
                    <Box direction="row" gap={8} isFullWidth justify={'center'}>
                        <UnlimitedButton
                            background="secondary"
                            width={214}
                            onClick={handleSavingCroppedImage}
                            data-testid={`unlimited-${index}-button-photo-save`}
                        >
                            <Text variant="bodyStrong" color="secondary_700">
                                <FormattedMessage
                                    id="annual.btn.save_card_owner_photo"
                                    defaultMessage="Zapisz zdjęcie"
                                />
                            </Text>
                        </UnlimitedButton>
                        <UnlimitedButton
                            background="error_bg"
                            width={48}
                            onClick={() => deletePhoto()}
                            data-testid={`unlimited-${index}-button-photo-delete`}
                        >
                            <Icon variant="bin" />
                        </UnlimitedButton>
                    </Box>
                </>
            ) : (
                <Box
                    direction="row"
                    gap={8}
                    isFullWidth
                    justify={'center'}
                    mt={12}
                >
                    <UnlimitedButton
                        background="secondary"
                        width={214}
                        onClick={() => setIsEditMode(true)}
                        data-testid={`unlimited-${index}-button-photo-edit`}
                    >
                        <Text variant="bodyStrong" color="secondary_700">
                            <FormattedMessage
                                id="annual.btn.edit_card_owner_photo"
                                defaultMessage="Edytuj"
                            />
                        </Text>
                    </UnlimitedButton>
                    <UnlimitedButton
                        background="secondary"
                        width={214}
                        onClick={() => changePhoto()}
                        data-testid={`unlimited-${index}-button-photo-change`}
                    >
                        <Text variant="bodyStrong" color="secondary_700">
                            <FormattedMessage
                                id="annual.btn.switch_card_owner_photo"
                                defaultMessage="Wgraj nowe"
                            />
                        </Text>
                    </UnlimitedButton>
                </Box>
            )}
        </UploadedPhotoWrapper>
    )
}

export default UploadedPhoto
